/**
 * Pure validation + parsing helper for the admin quote-revision form
 * (P16-T10 step 1). Mirrors `parseSpendingLimitInput` discipline (L26):
 * the route component (`QuoteDetail.tsx`) keeps form state as strings
 * (react-hook-form convention) and calls `parseRevisionForm` at submit
 * time to produce a typed `ReviseQuoteBody` (the wire shape from
 * `@wmart/shared-types`) or a per-field error map.
 *
 * Validations (must match the api-side `ReviseQuoteBodySchema` +
 * `AdminQuotesService.revise` runtime guards):
 *   - `valid_until` parseable as Date AND strictly in the future
 *   - `payment_terms_override` in the enum (or null to mean "inherit")
 *   - `items` non-empty (>= 1)
 *   - each `quantity` integer >= 1
 *   - each `quoted_unit_price_minor` integer >= 0 if present (empty
 *     string = null = "accept request-time price")
 *   - each `variant_id` non-empty (UUID format is validated server-side
 *     via `uuidLoose`)
 *
 * The helper takes a "now" injection point for deterministic tests
 * (mirrors the api-side `new Date()` in `AdminQuotesService.revise`).
 */
import type {
  QuotePaymentTermsOverride,
  ReviseQuoteBody,
} from "@wmart/shared-types";

/**
 * Item row as held in the form state. All numeric fields are strings
 * (react-hook-form keeps number inputs as strings until parse-on-submit).
 * `quoted_unit_price_minor` defaults to "" (the placeholder "use system
 * price"); we coerce "" → null at parse time.
 */
export interface ReviseFormItem {
  variant_id: string;
  /** Free-text numeric input — coerced via Number() at parse time. */
  quantity: string;
  /** Empty = null (accept request-time price); non-empty = parsed integer. */
  quoted_unit_price_minor: string;
}

/**
 * The form state shape used by `QuoteDetail.tsx`. Mirrors the
 * `ReviseQuoteBodySchema` shape with string-typed fields.
 */
export interface ReviseFormState {
  /** ISO-8601 UTC datetime string (output of <input type="datetime-local"> after toISOString). */
  valid_until: string;
  /** "" = inherit (no override); else one of the enum values. */
  payment_terms_override: "" | QuotePaymentTermsOverride;
  /** Max 2000 chars (server-side schema). "" allowed (omit from body). */
  admin_note: string;
  items: ReviseFormItem[];
}

/**
 * Discriminated result shape — either OK + body OR errors + no body.
 * The error map uses dotted paths for field-level surfacing:
 *   - "valid_until"
 *   - "payment_terms_override"
 *   - "items"                      (list-level, e.g. "at least 1 item")
 *   - "items.<idx>.quantity"
 *   - "items.<idx>.quoted_unit_price_minor"
 *   - "items.<idx>.variant_id"
 *
 * `ok === true` ⟺ `body` is defined; the caller can safely narrow.
 */
export interface ParseRevisionFormResult {
  ok: boolean;
  errors: Record<string, string>;
  body?: ReviseQuoteBody;
}

const PAYMENT_TERMS_VALUES: ReadonlyArray<QuotePaymentTermsOverride> = [
  "immediate",
  "net_30",
  "net_60",
];

/**
 * Parse + validate a revision-form snapshot. Pure function — no IO, no
 * Date.now() (the "now" anchor is injected for deterministic tests).
 *
 * @param form  Current form state (typically `getValues()` from RHF).
 * @param now   "Now" anchor; defaults to `new Date()`. Pass a fixed
 *              date in tests for stable assertions.
 * @returns     `{ ok, errors, body? }`.
 */
export function parseRevisionForm(
  form: ReviseFormState,
  now: Date = new Date(),
): ParseRevisionFormResult {
  const errors: Record<string, string> = {};

  // ---- valid_until: parseable + strictly future ----
  let validUntilIso: string | null = null;
  if (!form.valid_until || form.valid_until.trim() === "") {
    errors.valid_until = "Required";
  } else {
    const parsed = new Date(form.valid_until);
    if (Number.isNaN(parsed.getTime())) {
      errors.valid_until = "Must be a valid date/time";
    } else if (parsed.getTime() <= now.getTime()) {
      errors.valid_until = "Must be in the future";
    } else {
      validUntilIso = parsed.toISOString();
    }
  }

  // ---- payment_terms_override: enum or empty (inherit) ----
  let paymentTermsOverride: QuotePaymentTermsOverride | null = null;
  if (form.payment_terms_override !== "") {
    if (!PAYMENT_TERMS_VALUES.includes(form.payment_terms_override)) {
      errors.payment_terms_override =
        "Must be one of immediate, net_30, net_60 (or inherit)";
    } else {
      paymentTermsOverride = form.payment_terms_override;
    }
  }

  // ---- admin_note: optional, max 2000 chars ----
  let adminNote: string | undefined;
  if (form.admin_note && form.admin_note.length > 0) {
    if (form.admin_note.length > 2000) {
      errors.admin_note = "Max 2000 characters";
    } else {
      adminNote = form.admin_note;
    }
  }

  // ---- items: >=1 row; each row valid ----
  if (!Array.isArray(form.items) || form.items.length === 0) {
    errors.items = "At least 1 item required";
  } else if (form.items.length > 200) {
    errors.items = "Max 200 items";
  }

  const parsedItems: ReviseQuoteBody["items"] = [];
  if (Array.isArray(form.items)) {
    form.items.forEach((it, idx) => {
      // variant_id
      if (!it.variant_id || it.variant_id.trim() === "") {
        errors[`items.${idx}.variant_id`] = "Required";
      }

      // quantity — must be positive integer
      const qtyRaw = it.quantity?.trim() ?? "";
      let qtyNum: number | null = null;
      if (qtyRaw === "") {
        errors[`items.${idx}.quantity`] = "Required";
      } else if (!/^\d+$/.test(qtyRaw)) {
        errors[`items.${idx}.quantity`] = "Must be a positive integer";
      } else {
        const n = Number(qtyRaw);
        if (!Number.isInteger(n) || n < 1) {
          errors[`items.${idx}.quantity`] = "Must be >= 1";
        } else {
          qtyNum = n;
        }
      }

      // quoted_unit_price_minor — optional; if present, integer >= 0
      const quotedRaw = it.quoted_unit_price_minor?.trim() ?? "";
      let quotedNum: number | null = null;
      if (quotedRaw !== "") {
        if (!/^\d+$/.test(quotedRaw)) {
          errors[`items.${idx}.quoted_unit_price_minor`] =
            "Must be a non-negative integer (pence)";
        } else {
          const n = Number(quotedRaw);
          if (!Number.isInteger(n) || n < 0) {
            errors[`items.${idx}.quoted_unit_price_minor`] =
              "Must be >= 0";
          } else {
            quotedNum = n;
          }
        }
      }

      // Only push when this row has no row-level errors AND the
      // mandatory fields parsed cleanly.
      if (
        qtyNum !== null &&
        it.variant_id &&
        it.variant_id.trim() !== "" &&
        !errors[`items.${idx}.quoted_unit_price_minor`]
      ) {
        parsedItems.push({
          variant_id: it.variant_id,
          quantity: qtyNum,
          quoted_unit_price_minor: quotedNum,
        });
      }
    });
  }

  const hasErrors = Object.keys(errors).length > 0;
  if (hasErrors || validUntilIso === null) {
    return { ok: false, errors };
  }

  const body: ReviseQuoteBody = {
    valid_until: validUntilIso,
    payment_terms_override: paymentTermsOverride,
    ...(adminNote !== undefined ? { admin_note: adminNote } : {}),
    items: parsedItems,
  };
  return { ok: true, errors: {}, body };
}

/**
 * Live line totals — pure compute helper used by both `QuoteDetail` and
 * `QuoteItemsEditor` for the "totals" preview. Matches the api-side
 * `AdminQuotesService.findDetail` arithmetic exactly:
 *
 *   line_net   = effective_unit_price_minor * quantity
 *   line_vat   = round(line_net * vat_rate_basis_points / 10000)
 *   line_gross = line_net + line_vat
 *
 * `effective_unit_price_minor` is `quoted ?? requested` per the
 * `QuoteItemDetail` contract.
 */
export interface LineTotal {
  net: number;
  vat: number;
  gross: number;
}

export function computeLineTotal(
  effectiveUnitPriceMinor: number,
  quantity: number,
  vatRateBasisPoints: number,
): LineTotal {
  const net = effectiveUnitPriceMinor * quantity;
  const vat = Math.round((net * vatRateBasisPoints) / 10000);
  return { net, vat, gross: net + vat };
}
